/**
 * BackgroundScheduler -- Manages periodic background tasks for AWENI.
 *
 * Uses periodicBackgroundSync where supported, with a fallback that
 * checks due tasks on every app open using localStorage timestamps.
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ScheduledTask {
  id: string;
  label: string;
  intervalMs: number;
  /** If true, skip when battery saver is active */
  nonCritical: boolean;
  handler: () => Promise<void>;
}

export interface TaskStatus {
  id: string;
  lastRun: number;
  nextDue: number;
  running: boolean;
  lastError: string | null;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const STORAGE_PREFIX = 'aweni_task_';
const ONE_HOUR = 60 * 60 * 1000;
const ONE_DAY = 24 * ONE_HOUR;
const ONE_WEEK = 7 * ONE_DAY;

// ---------------------------------------------------------------------------
// BackgroundScheduler
// ---------------------------------------------------------------------------

export class BackgroundScheduler {
  private tasks = new Map<string, ScheduledTask>();
  private runningTasks = new Set<string>();
  private isBatterySaver: () => boolean;
  private _started = false;
  private _drainInterval: ReturnType<typeof setInterval> | null = null;
  private _onlineListener: (() => void) | null = null;

  /**
   * @param batterySaverCheck - Function that returns true when battery saver is active.
   *                           Injected to avoid circular dependency with DeviceManager.
   */
  constructor(batterySaverCheck?: () => boolean) {
    this.isBatterySaver = batterySaverCheck ?? (() => false);
  }

  // ---- Registration -------------------------------------------------------

  /**
   * Register all built-in AWENI background tasks.
   * Call once during app init, passing the actual handler implementations.
   */
  registerTasks(handlers: {
    checkUpdates: () => Promise<void>;
    syncPending: () => Promise<void>;
    refreshPrayerTimes: () => Promise<void>;
    cleanupCache: () => Promise<void>;
    checkStreaks: () => Promise<void>;
  }): void {
    this.register({
      id: 'daily-update-check',
      label: 'Check for app updates',
      intervalMs: ONE_DAY,
      nonCritical: true,
      handler: handlers.checkUpdates,
    });

    this.register({
      id: 'sync-pending',
      label: 'Sync offline actions',
      intervalMs: 15 * 60 * 1000, // 15 minutes
      nonCritical: false, // critical — user data
      handler: handlers.syncPending,
    });

    this.register({
      id: 'refresh-prayer-times',
      label: 'Recalculate prayer times',
      intervalMs: ONE_DAY,
      nonCritical: false,
      handler: handlers.refreshPrayerTimes,
    });

    this.register({
      id: 'cleanup-old-cache',
      label: 'Remove expired cache entries',
      intervalMs: ONE_WEEK,
      nonCritical: true,
      handler: handlers.cleanupCache,
    });

    this.register({
      id: 'check-streaks',
      label: 'Check streak status',
      intervalMs: ONE_DAY,
      nonCritical: true,
      handler: handlers.checkStreaks,
    });

    // Attempt to register periodicSync for each task
    this.registerPeriodicSync();
  }

  /**
   * Start the scheduler: register the sync-pending task (if not already set),
   * run any due tasks immediately, and set up a 60-second periodic drain of
   * the SyncEngine offline queue while the device is online.
   *
   * Idempotent — safe to call more than once; subsequent calls are no-ops.
   */
  async start(): Promise<void> {
    if (this._started) return;
    this._started = true;

    const { syncEngine } = await import('./SyncEngine');

    // Register the sync-pending task if not already provided by registerTasks().
    if (!this.tasks.has('sync-pending')) {
      this.register({
        id: 'sync-pending',
        label: 'Sync offline actions',
        intervalMs: 15 * 60 * 1000, // 15 minutes — checked on every runDueTasks()
        nonCritical: false, // user data — never skip
        handler: async () => { await syncEngine.processQueue(); },
      });
    }

    // Run any tasks that are already past due.
    this.runDueTasks().catch((err) => {
      console.warn('[BackgroundScheduler] runDueTasks failed on start:', err);
    });

    // Periodic drain every 60 s — honours navigator.onLine.
    this._drainInterval = setInterval(() => {
      if (navigator.onLine) {
        syncEngine.processQueue().catch(() => {});
      }
    }, 60_000);

    // Drain immediately whenever connectivity is restored.
    this._onlineListener = () => { syncEngine.processQueue().catch(() => {}); };
    window.addEventListener('online', this._onlineListener);
  }

  /**
   * Stop the periodic drain and remove the online listener.
   * Resets the started flag so start() can be called again if needed.
   */
  stop(): void {
    if (this._drainInterval !== null) {
      clearInterval(this._drainInterval);
      this._drainInterval = null;
    }
    if (this._onlineListener !== null) {
      window.removeEventListener('online', this._onlineListener);
      this._onlineListener = null;
    }
    this._started = false;
  }

  /**
   * Register a single custom task.
   */
  register(task: ScheduledTask): void {
    this.tasks.set(task.id, task);
  }

  // ---- Execution ----------------------------------------------------------

  /**
   * Run all tasks that are currently due. Call on every app open.
   */
  async runDueTasks(): Promise<void> {
    const now = Date.now();

    for (const [id, task] of this.tasks) {
      if (this.runningTasks.has(id)) continue;

      // Skip non-critical tasks when battery saver is active
      if (task.nonCritical && this.isBatterySaver()) continue;

      const lastRun = this.getLastRun(id);
      const nextDue = lastRun + task.intervalMs;

      if (now >= nextDue) {
        // Run without awaiting all — they execute in parallel
        this.executeTask(id).catch(() => {
          // Logged inside executeTask
        });
      }
    }
  }

  /**
   * Run a specific task by ID, regardless of schedule.
   */
  async runDueTask(taskId: string): Promise<void> {
    await this.executeTask(taskId);
  }

  /**
   * Get the status of all registered tasks.
   */
  getTaskStatus(): TaskStatus[] {
    const result: TaskStatus[] = [];
    for (const [id, task] of this.tasks) {
      const lastRun = this.getLastRun(id);
      result.push({
        id,
        lastRun,
        nextDue: lastRun + task.intervalMs,
        running: this.runningTasks.has(id),
        lastError: this.getLastError(id),
      });
    }
    return result;
  }

  /**
   * Get status of a single task.
   */
  getTaskStatusById(taskId: string): TaskStatus | null {
    const task = this.tasks.get(taskId);
    if (!task) return null;
    const lastRun = this.getLastRun(taskId);
    return {
      id: taskId,
      lastRun,
      nextDue: lastRun + task.intervalMs,
      running: this.runningTasks.has(taskId),
      lastError: this.getLastError(taskId),
    };
  }

  // ---- Internal -----------------------------------------------------------

  private async executeTask(taskId: string): Promise<void> {
    const task = this.tasks.get(taskId);
    if (!task) return;
    if (this.runningTasks.has(taskId)) return;

    this.runningTasks.add(taskId);
    try {
      await task.handler();
      this.setLastRun(taskId, Date.now());
      this.clearLastError(taskId);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      this.setLastError(taskId, message);
    } finally {
      this.runningTasks.delete(taskId);
    }
  }

  // ---- Periodic Sync (browser API) ---------------------------------------

  private async registerPeriodicSync(): Promise<void> {
    try {
      const reg = await navigator.serviceWorker?.ready;
      if (!reg || !('periodicSync' in reg)) return;

      const ps = reg as ServiceWorkerRegistration & {
        periodicSync: {
          register(tag: string, opts: { minInterval: number }): Promise<void>;
          getTags(): Promise<string[]>;
        };
      };

      const existingTags = await ps.periodicSync.getTags();

      for (const [id, task] of this.tasks) {
        if (!existingTags.includes(id)) {
          try {
            await ps.periodicSync.register(id, { minInterval: task.intervalMs });
          } catch {
            // Permission denied or not supported for this interval
          }
        }
      }
    } catch {
      // periodicSync not available — relying on app-open fallback
    }
  }

  // ---- localStorage helpers -----------------------------------------------

  private getLastRun(taskId: string): number {
    const raw = localStorage.getItem(`${STORAGE_PREFIX}${taskId}_lastRun`);
    return raw ? parseInt(raw, 10) : 0;
  }

  private setLastRun(taskId: string, time: number): void {
    localStorage.setItem(`${STORAGE_PREFIX}${taskId}_lastRun`, String(time));
  }

  private getLastError(taskId: string): string | null {
    return localStorage.getItem(`${STORAGE_PREFIX}${taskId}_error`);
  }

  private setLastError(taskId: string, message: string): void {
    localStorage.setItem(`${STORAGE_PREFIX}${taskId}_error`, message);
  }

  private clearLastError(taskId: string): void {
    localStorage.removeItem(`${STORAGE_PREFIX}${taskId}_error`);
  }
}

// ---------------------------------------------------------------------------
// Singleton (battery saver check injected later via init)
// ---------------------------------------------------------------------------

export const backgroundScheduler = new BackgroundScheduler();
